/**
 * Sidebar Token Usage - Compact monthly token meter for the Results sidebar
 *
 * Mirrors the Dashboard "Usage This Month" module, showing remaining tokens,
 * percent used, and an upgrade prompt when nearing or exceeding limits.
 */

import React, { useEffect, useState } from 'react';
import { entitlementService } from '../../../services/entitlement';
import { fetchDashboardStats } from '../../../lib/api/dashboard';
import { Tooltip } from '../../shared/Tooltip';

interface SidebarTokenUsageProps {
  collapsed?: boolean;
}

interface TokenUsageData {
  remaining: number;
  percentage: number;
  limit: number;
  used: number;
  allowed: boolean;
  upgradeUrl?: string;
  planDisplayName?: string;
  progressColor: string;
  status: 'ok' | 'warning' | 'limit-reached';
}

export const SidebarTokenUsage: React.FC<SidebarTokenUsageProps> = ({ collapsed = false }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [usage, setUsage] = useState<TokenUsageData | null>(null);
  const [isUnlimited, setIsUnlimited] = useState(false);
  const [planName, setPlanName] = useState<string | undefined>();

  console.log('[SidebarTokenUsage] Component mounted/rendered', { collapsed, isLoading });

  useEffect(() => {
    const fetchTokenUsage = async () => {
      try {
        console.log('[SidebarTokenUsage] Fetching token usage...');
        setIsLoading(true);
        const status = await entitlementService.getStatus();
        console.log('[SidebarTokenUsage] Entitlement status received:', {
          paywallEnabled: status.paywallEnabled,
          hasTokenQuota: !!status.quotas?.tokens,
          tokenLimit: status.quotas?.tokens?.limit,
          tokenUsed: status.quotas?.tokens?.used,
          planName: status.plan?.displayName
        });

        if (!status.paywallEnabled) {
          console.log('[SidebarTokenUsage] Paywall disabled, hiding module');
          setUsage(null);
          return;
        }

        // Create tokenQuota with fallback to dashboard stats (like the dashboard does)
        // This ensures token usage displays even if entitlementStatus.quotas.tokens is undefined
        let tokens = status.quotas?.tokens;

        if (!tokens) {
          console.log('[SidebarTokenUsage] No token quota from entitlements, fetching from dashboard stats...');
          try {
            const stats = await fetchDashboardStats();
            console.log('[SidebarTokenUsage] Dashboard stats received:', {
              monthlyTokenUsage: stats.monthlyTokenUsage,
              monthlyTokenLimit: stats.monthlyTokenLimit,
              hasCost: stats.monthlyTokenCostUsd !== undefined
            });

            // Build token quota from dashboard stats
            tokens = {
              used: stats.monthlyTokenUsage,
              limit: stats.monthlyTokenLimit,
              remaining: Math.max(0, stats.monthlyTokenLimit - stats.monthlyTokenUsage),
              percentage: stats.monthlyTokenLimit > 0
                ? (stats.monthlyTokenUsage / stats.monthlyTokenLimit) * 100
                : 0,
              allowed: stats.monthlyTokenUsage < stats.monthlyTokenLimit,
            };

            console.log('[SidebarTokenUsage] Built token quota from dashboard stats:', tokens);
          } catch (dashboardError) {
            console.error('[SidebarTokenUsage] Failed to fetch dashboard stats fallback:', dashboardError);
            setUsage(null);
            return;
          }
        }

        if (!tokens) {
          console.log('[SidebarTokenUsage] No token quota data after fallback, hiding module');
          setUsage(null);
          return;
        }

        if (tokens.limit === -1) {
          console.log('[SidebarTokenUsage] Unlimited tokens detected');
          setIsUnlimited(true);
          setPlanName(status.plan?.displayName);
          setUsage(null);
          return;
        }

        const percentage = tokens.percentage ?? (
          tokens.limit > 0 ? (tokens.used / tokens.limit) * 100 : 0
        );

        const progressColor =
          percentage >= 100 ? 'bg-red-500' :
          percentage >= 80 ? 'bg-amber-500' :
          percentage >= 60 ? 'bg-yellow-500' :
          'bg-green-500';

        const statusLabel: TokenUsageData['status'] =
          percentage >= 100 ? 'limit-reached' :
          percentage >= 80 ? 'warning' :
          'ok';

        console.log('[SidebarTokenUsage] Setting usage data:', {
          remaining: Math.max(0, tokens.remaining),
          percentage,
          limit: tokens.limit,
          used: tokens.used,
          status: statusLabel
        });

        setUsage({
          remaining: Math.max(0, tokens.remaining),
          percentage,
          limit: tokens.limit,
          used: tokens.used,
          allowed: tokens.allowed,
          planDisplayName: status.plan?.displayName,
          upgradeUrl: status.upgradeUrl,
          progressColor,
          status: statusLabel
        });
      } catch (error) {
        console.error('[SidebarTokenUsage] Failed to fetch token usage:', error);
        setUsage(null);
      } finally {
        console.log('[SidebarTokenUsage] Fetch complete, setting isLoading=false');
        setIsLoading(false);
      }
    };

    fetchTokenUsage();
  }, []);

  console.log('[SidebarTokenUsage] Render decision:', { isLoading, isUnlimited, hasUsage: !!usage });

  if (isLoading) {
    console.log('[SidebarTokenUsage] Rendering: null (still loading)');
    return null;
  }

  if (isUnlimited) {
    console.log('[SidebarTokenUsage] Rendering: unlimited message');
    return (
      <div className="px-3 sm:px-4 py-3 sm:py-4 border-t border-slate-700/50">
        <h3 className="text-xs sm:text-sm font-semibold text-slate-400 uppercase tracking-wider mb-2 sm:mb-3">
          Token Usage
        </h3>
        <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-3">
          <div className="text-sm text-green-400 font-semibold">
            Unlimited tokens on {planName || 'your plan'}
          </div>
        </div>
      </div>
    );
  }

  if (!usage) {
    console.log('[SidebarTokenUsage] Rendering: null (no usage data)');
    return null;
  }

  console.log('[SidebarTokenUsage] Rendering: usage module', { collapsed });

  if (collapsed) {
    return (
      <div className="flex flex-col items-center py-3">
        <div className="text-xs font-medium text-slate-300 whitespace-nowrap">
          {usage.remaining.toLocaleString()} left · {usage.percentage.toFixed(0)}% used
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 sm:px-4 py-3 sm:py-4 border-t border-slate-700/50">
      <div className="flex items-center gap-2 mb-2 sm:mb-3">
        <h3 className="text-xs sm:text-sm font-semibold text-slate-400 uppercase tracking-wider">
          Token Usage
        </h3>
        <Tooltip content="Tokens are used when you score responses or run consensus analysis on comparisons." />
      </div>

      <div className="space-y-2">
        {/* Token usage percentage only */}
        <div className="text-sm text-slate-300">
          <span className="font-medium">{usage.percentage.toFixed(0)}%</span> of monthly tokens used
        </div>

        {/* Progress bar */}
        <div className="w-full bg-slate-700 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${usage.progressColor}`}
            style={{ width: `${Math.min(usage.percentage, 100)}%` }}
          />
        </div>

        {/* Reset date */}
        <div className="text-xs text-slate-400">
          Resets monthly on 1st
        </div>

        {/* Upgrade link - hide when limit reached since warning box has its own CTA */}
        {usage.upgradeUrl && (usage.percentage >= 60 || usage.remaining <= 5000) && usage.status !== 'limit-reached' && (
          <div className="flex justify-end">
            <a
              href={usage.upgradeUrl}
              className="text-xs text-cyan-400 hover:text-cyan-300 font-medium transition-colors"
            >
              Upgrade →
            </a>
          </div>
        )}

        {/* Limit reached warning */}
        {usage.status === 'limit-reached' && (
          <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-2 text-xs">
            <div className="text-red-400 font-semibold mb-1">Token limit reached</div>
            <div className="text-red-300">
              <a
                href={usage.upgradeUrl || '/auth/signin'}
                className="underline hover:text-red-200"
              >
                Upgrade for more tokens
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
